import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from "../../context/auth";
import GoogleMapReact from 'google-map-react'
import IconButton from '@material-ui/core/IconButton'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import HTMLTooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import InfoDialog from '../display/InfoDialog'
import PipelineComponent from './PipelineComponent'
import PADDComponent from './PADDComponent'
import RailComponent from './RailComponent'
import MarineComponent from './MarineComponent'
import TerminalComponent from './TerminalComponent'
import TerminalCsvComponent from './TerminalCsvComponent'
import MapCitySearch from './MapCitySearch';
import RackPriceMarker from './RackPriceMarker';
import { useRackPricing } from '../../context/rackPricing';

const inquiryTheme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5'
    }
  }
})

const overlayDataCache = {
  data: null,
  promise: null
};

const getBufferedBounds = (bounds, paddingRatio = 0.2) => {
  if (!bounds || !bounds.nw || !bounds.se) return null;

  const latSpan = Math.abs(bounds.nw.lat - bounds.se.lat);
  const lngSpan = Math.abs(bounds.se.lng - bounds.nw.lng);
  const latPadding = latSpan * paddingRatio;
  const lngPadding = lngSpan * paddingRatio;

  return {
    north: Math.min(90, bounds.nw.lat + latPadding),
    south: Math.max(-90, bounds.se.lat - latPadding),
    west: bounds.nw.lng - lngPadding,
    east: bounds.se.lng + lngPadding,
  };
};

const isLongitudeInBounds = (lng, west, east) => {
  if (west < -180) {
    return lng >= west + 360 || lng <= east;
  }

  if (east > 180) {
    return lng >= west || lng <= east - 360;
  }

  return lng >= west && lng <= east;
};

const isPointInBounds = (lat, lng, bounds) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (!bounds || Number.isNaN(latitude) || Number.isNaN(longitude)) return true;

  return (
    latitude <= bounds.north &&
    latitude >= bounds.south &&
    isLongitudeInBounds(longitude, bounds.west, bounds.east)
  );
};

const filterRowsByLatLng = (rows, bounds, getLatLng) => {
  if (!rows || !bounds) return rows || [];

  return rows.filter((row) => {
    const { lat, lng } = getLatLng(row);
    return isPointInBounds(lat, lng, bounds);
  });
};

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Request failed (${response.status}) for ${url}`);
  }
  return response.json();
};

const fetchAllRecords = async (url, outFields = '*', additionalFilters='') => {
  let allRecords = [];
  let offset = 0;
  const limit = 1000;
  let hasMoreRecords = true;

  while (hasMoreRecords) {
    const data = await fetchJson(`${url}?outFields=${outFields}&where=1%3D1${additionalFilters}&f=geojson&resultOffset=${offset}&resultRecordCount=${limit}`);

    if (data.features && data.features.length > 0) {
      allRecords = allRecords.concat(data.features);
      offset += limit;
    } else {
      hasMoreRecords = false;
    }
  }

  return allRecords;
};

const safeLoad = async (request, fallback) => {
  try {
    return await request;
  } catch {
    return fallback;
  }
};

const publicationDetails = {
  TR: 'Morning (TR)',
  TE: 'Evening (TE)',
};

const assessmentTime = (price) => {
  const timestamp = Date.parse(price?.assessDate || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const assessmentDay = (price) => String(price?.assessDate || '').slice(0, 10);

const mergeRackPriceCities = (morningCities = [], eveningCities = [], publication = 'LATEST') => {
  const cities = new Map();

  const addCities = (sourceCities, timeCode) => {
    sourceCities.forEach((city) => {
      const cityEntry = cities.get(city.code) || { ...city, pricesBySymbol: new Map() };
      cityEntry.city = city.city;
      cityEntry.state = city.state;
      cityEntry.latitude = city.latitude;
      cityEntry.longitude = city.longitude;

      city.prices.forEach((price) => {
        const symbolEntry = cityEntry.pricesBySymbol.get(price.symbol) || {};
        symbolEntry[timeCode] = {
          ...price,
          timeCode,
          publication: publicationDetails[timeCode],
        };
        cityEntry.pricesBySymbol.set(price.symbol, symbolEntry);
      });
      cities.set(city.code, cityEntry);
    });
  };

  addCities(morningCities, 'TR');
  addCities(eveningCities, 'TE');

  return Array.from(cities.values()).map(({ pricesBySymbol, ...city }) => {
    const prices = Array.from(pricesBySymbol.values()).map((pricesByTimeCode) => {
      const morning = pricesByTimeCode.TR;
      const evening = pricesByTimeCode.TE;

      if (publication === 'TR') return morning;
      if (publication === 'TE') {
        if (evening && (!morning || assessmentDay(evening) >= assessmentDay(morning))) {
          return evening;
        }
        return morning ? {
          ...morning,
          timeCode: 'TE',
          publication: 'Evening (TE) — not published',
          value: null,
          previousValue: null,
          previousDate: null,
          change: null,
          changePercent: null,
          assessDate: null,
          unavailable: true,
        } : undefined;
      }

      if (!morning) return evening;
      if (!evening) return morning;
      return assessmentTime(evening) >= assessmentTime(morning) ? evening : morning;
    }).filter(Boolean);

    return { ...city, prices: prices.sort((a, b) => a.symbol.localeCompare(b.symbol)) };
  }).filter((city) => city.prices.length > 0)
    .sort((a, b) => a.city.localeCompare(b.city));
};

const loadOverlayData = () => {
  if (overlayDataCache.data) {
    return Promise.resolve(overlayDataCache.data);
  }

  if (overlayDataCache.promise) {
    return overlayDataCache.promise;
  }

  const tokenHeaders = {
    'x-access-token': sessionStorage.getItem('token')
  };

  overlayDataCache.promise = Promise.all([
    safeLoad(fetchJson('/api/context/csvTerminals', { method: 'GET', headers: tokenHeaders }), { data: [] }),
    safeLoad(fetchJson('/api/context/terminals', { method: 'GET', headers: tokenHeaders }), { data: [] }),
    safeLoad(fetchAllRecords('https://arcgis.netl.doe.gov/server/rest/services/Hosted/EIA_pipeline_data/FeatureServer/2/query'), []),
    safeLoad(fetchJson('/api/context/product_terminals', { method: 'GET', headers: tokenHeaders }), { data: [] }),
    safeLoad(fetchAllRecords('https://arcgis.netl.doe.gov/server/rest/services/Hosted/EIA_pipeline_data/FeatureServer/1/query'), []),
    safeLoad(fetchAllRecords('https://services7.arcgis.com/FGr1D95XCGALKXqM/arcgis/rest/services/PADD_EIA/FeatureServer/0/query'), []),
    safeLoad(fetchAllRecords('https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_North_American_Rail_Network_Lines_BNSF/FeatureServer/0/query'), []),
    safeLoad(fetchAllRecords('https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_North_American_Rail_Network_Lines_CSXT/FeatureServer/0/query'), []),
    safeLoad(fetchAllRecords('https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_North_American_Rail_Network_Lines_UP/FeatureServer/0/query'), []),
    safeLoad(fetchAllRecords('https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_North_American_Rail_Network_Lines_NS/FeatureServer/0/query'), []),
    safeLoad(fetchAllRecords('https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_Marine_Highways/FeatureServer/0/query'), []),
    safeLoad(fetchAllRecords('https://services.arcgis.com/xOi1kZaI0eWDREZv/arcgis/rest/services/NTAD_Intermodal_Freight_Facilities_Pipeline_Terminals/FeatureServer/0/query'), [])
  ])
  .then(([
    additionalTerminalsResponse,
    terminalDetailResponse,
    crudePipelineData,
    productTerminalsResponse,
    refinedPipelineData,
    paddData,
    railBNSFData,
    railCSXData,
    railUPData,
    railNSData,
    marineData,
    intermodalData
  ]) => {
    const data = {
      additionalTerminalsAll: additionalTerminalsResponse.data || [],
      csvData: terminalDetailResponse.data || [],
      crudePipeline: { type: 'FeatureCollection', features: crudePipelineData },
      productTerminals: productTerminalsResponse.data || [],
      refinedPipeline: { type: 'FeatureCollection', features: refinedPipelineData },
      padd: { features: paddData },
      railBNSF: { type: 'FeatureCollection', features: railBNSFData },
      railCSX: { type: 'FeatureCollection', features: railCSXData },
      railUP: { type: 'FeatureCollection', features: railUPData },
      railNS: { type: 'FeatureCollection', features: railNSData },
      marine: { type: 'FeatureCollection', features: marineData },
      intermodalAll: intermodalData
    };

    overlayDataCache.data = data;
    return data;
  })
  .catch((error) => {
    overlayDataCache.promise = null;
    throw error;
  });

  return overlayDataCache.promise;
};
 
function MapComponent ({id, volume, price, product, value}) {
  const [info, setInfo] = useState(false)

  const handleCloseInfo = () => {
    setInfo(false)
  }

  return (
    <div style = {{'zIndex':6}}>
      <InfoDialog
        open = {info}
        onClose = {handleCloseInfo}
        type = 'listing_info'
        id = {id}
      />
      <HTMLTooltip
        title = {
              <Typography>
                Price : {price} ($/BBL)<br/>
                Product : {product} <br/>
                Volume : {volume} (MBBL)<br/>
              </Typography>
        }
        onClick = {() => {setInfo(true)}}>
        <IconButton
          size = 'small'
        >
            <LocationOnIcon color = 'primary'/>
        </IconButton>
      </HTMLTooltip>
    </div>
  )
}

function InquiryMapComponent ({id, min_volume, max_volume, price, product, value}) {
  const [info, setInfo] = useState(false)
  const handleCloseInfo = () => {
    setInfo(false)
  }

  return (
    <div style = {{'zIndex':6}}>
      <InfoDialog
        open = {info}
        onClose = {handleCloseInfo}
        type = 'inquiry_info'
        id = {id}
      />
      <HTMLTooltip
        title = {
              <Typography>
                Price : {price} ($/BBL)<br/>
                Product : {product} <br/>
                Volume Range : {min_volume} - {max_volume} (MBBL)<br/>
              </Typography>

        }
        onClick = {() => setInfo(true)}>
        <IconButton
          size = 'small'
        >
          <ThemeProvider theme = {inquiryTheme}>
            <LocationOnIcon color = 'primary'/>
          </ThemeProvider>
        </IconButton>
      </HTMLTooltip>
    </div>

  )
}

export default function ListingMap({listings, inquirys, showInquirys, showListings, overlay, subOverlay, setSubOverlay, setCsvPipelineOptions, setCrudePipelineOptions, setRefinedPipelineOptions, crudeFilters, refinedFilters, additionalTerminalFilters, rackPricingFilters = { products: [], categories: [], publication: 'LATEST' }, setRackPricingFilters, setSourceOptions, citySearch, setCitySearch, setOverlayTableData, showRackPricingTable = false, height = '100%'}){
  const defaultMapOptions = (mapApi) => ({
    fullscreenControl: false,
    mapTypeControl: true,
    mapTypeControlOptions: {
      position: mapApi.ControlPosition.TOP_RIGHT,
      mapTypeIds: ['roadmap', 'satellite']
    },
    styles: [
      { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
      {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: 'poi',
        stylers: [{visibility: 'off'}]
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#263c3f" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b9a76" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#38414e" }],
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#212a37" }],
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca5b3" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#746855" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1f2835" }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#f3d19c" }],
      },
      {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{ color: "#2f3948" }],
      },
      {
        featureType: "transit.station",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: 'transit',
        elementType: 'labels.icon',
        stylers: [{visibility: 'off'}]
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#17263c" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#515c6d" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#17263c" }],
      },
    ]
  });

  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const [viewportBounds, setViewportBounds] = useState(null);
  //data
  const { overlayAccess } = useAuth()
  const { rackPricingCache, loadRackPricing } = useRackPricing()
  const [crudePipeline, setCrudePipeline] = useState(null);
  const [filteredCrudePipelines, setFilteredCrudePipelines] = useState(null)
  const [refinedPipeline, setRefinedPipeline] = useState(null);
  const [filteredRefinedPipelines, setFilteredRefinedPipelines] = useState(null)
  const [padd, setPadd] = useState(null);
  const [productTerminals, setProductTerminals] = useState(null)
  const [railBNSF, setRailBNSF] = useState(null)
  const [railCSX, setRailCSX] = useState(null)
  const [railUP, setRailUP] = useState(null)
  const [railNS, setRailNS] = useState(null)
  const [marine, setMarine] = useState(null)
  const [intermodal, setIntermodal] = useState(null)
  const [intermodalAll, setIntermodalAll] = useState(null)
  const [terminalDetail, setTerminalDetail] = useState(null)
  const [csvData, setCsvData] = useState([])
  const [additionalTerminalsAll, setAdditionalTerminalsAll] = useState([])
  const [additionalTerminals, setAdditionalTerminals] = useState([])
  const cachedRackPriceCities = mergeRackPriceCities(
    rackPricingCache.TR?.data?.cities,
    rackPricingCache.TE?.data?.cities,
    rackPricingFilters.publication
  )
  const [rackPriceCities, setRackPriceCities] = useState(cachedRackPriceCities)
  const filteredRackPriceCities = useMemo(() => {
    const selectedProducts = rackPricingFilters.products || [];
    const selectedCategories = rackPricingFilters.categories || [];

    return rackPriceCities.map((city) => ({
      ...city,
      prices: city.prices.filter((price) => (
        (selectedProducts.length === 0 || selectedProducts.includes(price.specCode))
        && (selectedCategories.length === 0 || selectedCategories.includes(price.productCategoryCode))
      )),
    })).filter((city) => city.prices.length > 0);
  }, [rackPriceCities, rackPricingFilters]);

  useEffect(() => {
    if (csvData && csvData.length > 0) {
      const pipelines = getAllCsvPipelineNames(csvData);
      setCsvPipelineOptions(pipelines);
    }
  }, [csvData]);
 
  useEffect(() => {
    if (crudePipeline && crudePipeline.features && crudePipeline.features.length > 0)
    {
      const pipelines = getAllCrudePipelineNames(crudePipeline)
      setCrudePipelineOptions(pipelines)
    }
  }, [crudePipeline])

  useEffect(() => {
    if (refinedPipeline && refinedPipeline.features && refinedPipeline.features.length > 0) {
      const pipelines = getAllCrudePipelineNames(refinedPipeline)
      setRefinedPipelineOptions(pipelines)
    }
  }, [refinedPipeline])

  useEffect(() => {
    if(additionalTerminalsAll && additionalTerminalsAll.length > 0){
      const options = getAllSources(additionalTerminalsAll);
      setSourceOptions(options);
    }
  }, [additionalTerminalsAll])


const applyOverlayData = (data) => {
  setAdditionalTerminalsAll(data.additionalTerminalsAll);
  setCsvData(data.csvData);
  findMaxDetailCapacity(data.csvData);
  setCrudePipeline(data.crudePipeline);
  setProductTerminals(data.productTerminals);
  setRefinedPipeline(data.refinedPipeline);
  setPadd(data.padd);
  setRailBNSF(data.railBNSF);
  setRailCSX(data.railCSX);
  setRailUP(data.railUP);
  setRailNS(data.railNS);
  setMarine(data.marine);
  setIntermodal(data.intermodalAll);
  setIntermodalAll(data.intermodalAll);
  findMaxCapacity(data.intermodalAll);
};

const fetchData = async () => {
  try {
    const data = await loadOverlayData();
    applyOverlayData(data);
  } catch {
    // Individual overlay requests already provide safe fallback values.
  }
};

const findMaxDetailCapacity = (data) => {
  var maxCap = 0
  data.forEach((item) => {
    if (Number(item['Storage']) > maxCap){
      maxCap = Number(item['Storage'])
    }
  });
  setSubOverlay(prev => ({...prev, 'detail_maxCapacity': maxCap}))
}

const findMaxCapacity = (data) => {
  var maxCap = 0
  data.forEach((item) => {
    if (Number(item.properties.CAPACITY) > maxCap) {

      maxCap = Number(item.properties.CAPACITY)
    }
  });
  setSubOverlay(prev => ({...prev, 'maxCapacity': maxCap}))
}

useEffect(() => {
  const filtered = filterTerminals(intermodalAll, subOverlay)
  setIntermodal(filtered)

},[subOverlay,intermodalAll])

useEffect(() => {
  const filtered_detail = filterDetail(csvData, subOverlay)
  setTerminalDetail(filtered_detail)
}, [subOverlay, csvData])

useEffect(() => {
  const filteredCrude = filterPipelines(
    crudePipeline,
    crudeFilters.crude_pipelines,
    'pipename',
    overlay.crude
  );
  setFilteredCrudePipelines(filteredCrude);
}, [crudeFilters, crudePipeline, overlay.crude]);

useEffect(() => {
  const filteredRefined = filterPipelines(
    refinedPipeline,
    refinedFilters.refined_pipelines,
    'pipename',
    overlay.refined
  );
  setFilteredRefinedPipelines(filteredRefined);
}, [refinedFilters, refinedPipeline, overlay.refined]);


useEffect(() => {
  const filteredTerminals = filterAdditional(additionalTerminalsAll, additionalTerminalFilters.source)
  setAdditionalTerminals(filteredTerminals)
}, [additionalTerminalFilters, additionalTerminalsAll])

const filterAdditional = (terminals, filters) => {
  if (!terminals || terminals.length === 0) return [];

  // If no filter is selected, return all
  if (!filters || filters.length === 0) return terminals;

  // Normalize all selected filter names safely
  const normalizedFilters = (filters || [])
    .filter(Boolean)
    .map(f => String(f).trim().toLowerCase());

  // Filter terminals based on "source" field
  return terminals.filter(t => {
    const sourceName = String(t.source || t.Source || '').trim().toLowerCase();
    return normalizedFilters.includes(sourceName);
  });
};

const filterDetail = (terminals, filters) => {
  if (!terminals) return [];

  // --- Normalize pipeline filters first ---
  // Handle undefined, false, or boolean true consistently
  const normalizePipeline = (val) => {
    if (Array.isArray(val)) return val;         // keep arrays (like ['BASF'])
    if (val === true) return [];                // toggle true but no specific names
    return [];                                  // false/undefined/null -> empty array
  };

  const inFilter  = normalizePipeline(filters.detail_pipeline_in);
  const outFilter = normalizePipeline(filters.detail_pipeline_out);

  // --- Check if any filters are active ---
  const productKeys = [
    'detail_crude','detail_asphalt','detail_chemicals','detail_lng',
    'detail_propane','detail_butane','detail_refined_products',
    'detail_ethanol','detail_biodiesel'
  ];
  const transportKeys = [
    'detail_truck_in','detail_truck_out',
    'detail_marine_in','detail_marine_out',
    'detail_rail_in','detail_rail_out'
  ];

  const allProductOff = productKeys.every(k => !filters[k]);
  const allTransportOff = transportKeys.every(k => !filters[k]);
  const capacityInactive = !filters.detail_storage ||
    (filters.detail_storage[0] === 0 && filters.detail_storage[1] === 0);

  const noPipelineNames = inFilter.length === 0 && outFilter.length === 0;

  // ✅ if absolutely nothing active → show all
  if (allProductOff && allTransportOff && capacityInactive && noPipelineNames) {
    return terminals;
  }

  return terminals.filter(terminal => {
    // --- PRODUCT FILTER ---
    const productMatches = (
      (filters.detail_crude && terminal['Crude Oil'] === 'Y') ||
      (filters.detail_asphalt && terminal['Asphalt'] === 'Y') ||
      (filters.detail_chemicals && terminal['Chemicals'] === 'Y') ||
      (filters.detail_lng && terminal['LNGs'] === 'Y') ||
      (filters.detail_propane && terminal['Propane'] === 'Y') ||
      (filters.detail_butane && terminal['Butane'] === 'Y') ||
      (filters.detail_refined_products && terminal['Refined Products'] === 'Y') ||
      (filters.detail_ethanol && terminal['Ethanol'] === 'Y') ||
      (filters.detail_biodiesel && terminal['Biodiesel/Renewables'] === 'Y')
    );

    // --- TRANSPORT FILTER ---
    const transportMatches = (
      (filters.detail_truck_in && terminal['Truck In'] === 'Yes') ||
      (filters.detail_truck_out && terminal['Truck Out'] === 'Yes') ||
      (filters.detail_marine_in && terminal['Marine In'] === 'Yes') ||
      (filters.detail_marine_out && terminal['Marine Out'] === 'Yes') ||
      (filters.detail_rail_in && terminal['Rail In'] === 'Yes') ||
      (filters.detail_rail_out && terminal['Rail Out'] === 'Yes')
    );

    // --- PIPELINE FILTER ---
    let pipelineMatches = true;

    const pipeIn  = (terminal['Pipeline In']  || '').toLowerCase();
    const pipeOut = (terminal['Pipeline Out'] || '').toLowerCase();

    const lowerIn  = inFilter.map(p => p.toLowerCase().trim());
    const lowerOut = outFilter.map(p => p.toLowerCase().trim());

    const hasIn  = lowerIn.length > 0;
    const hasOut = lowerOut.length > 0;

    if (hasIn || hasOut) {
      const inboundMatch  = hasIn  && lowerIn.some(name => pipeIn.includes(name));
      const outboundMatch = hasOut && lowerOut.some(name => pipeOut.includes(name));
      pipelineMatches = inboundMatch || outboundMatch;
    }

    // --- CAPACITY FILTER ---
    let capacityMatches = true;
    if (filters.detail_storage) {
      const terminalCap = Number(terminal['Storage']) || 0;
      const [minCap, maxCap] = filters.detail_storage;
      if (!(minCap === 0 && maxCap === 0)) {
        capacityMatches = terminalCap >= minCap && terminalCap <= maxCap;
      }
    }

    // --- COMBINE ALL ---
    const includeByProduct = allProductOff || productMatches;
    const includeByTransport = allTransportOff || transportMatches;

    return includeByProduct && includeByTransport && pipelineMatches && capacityMatches;
  });
};


const filterTerminals = (terminals, filters) => {
    if (terminals){
      // Check if all product and transport filters are unchecked (all false)
      const allProductFiltersUnchecked = Object.values(filters).slice(3,12).every(filter => filter === false);
      const allTransportFiltersUnchecked = Object.values(filters).slice(0,3).every(filter => filter === false);
      const capacityFilterInActive = filters.capacity[1] == 0
      // If no filters are selected, return all terminals
      if (allProductFiltersUnchecked && allTransportFiltersUnchecked && capacityFilterInActive) return terminals;
      return terminals.filter(terminal => {
        const {crude, refined, pet_chem, ngl, gasoline, distillate, biodiesel, jetfuel, ethanol,
          truck, rail, water
        } = filters

      //Check if at least one product filter matches
        const productMatches =
          (crude && terminal.properties.CRUDE_OIL === 'Y') ||
          (refined && terminal.properties.REFINED === 'Y') ||
          (pet_chem && terminal.properties.PET_CHEM === 'Y') ||
          (ngl && terminal.properties.NGL === 'Y') ||
          (gasoline && terminal.properties.GASOLINE === 'Y') ||
          (distillate && terminal.properties.DISTILLATE === 'Y') ||
          (biodiesel && terminal.properties.BIODIESEL === 'Y') ||
          (jetfuel && terminal.properties.JETFUEL === 'Y') ||
          (ethanol && terminal.properties.ETHANOL === 'Y')

        // Check if at least one transport filter matches
        const transportMatches =
          (truck && terminal.properties.TRUCK === 'Y') ||
          (rail && terminal.properties.RAIL === 'Y') ||
          (water && terminal.properties.WATER === 'Y');

        //capacity filter
        let capacityMatches = true;
        if(filters.capacity){
          const terminalCapacity = Number(terminal.properties.CAPACITY) || 0;
          if (filters.capacity !== undefined) {
            capacityMatches = capacityMatches && (terminalCapacity >= filters.capacity[0]);
          }
          if (filters.capacity !== undefined) {
            capacityMatches = capacityMatches && (terminalCapacity <= filters.capacity[1])
          }
        }

        // If there are no product filters or transport filters selected, include terminal
        const includeByProduct = allProductFiltersUnchecked || productMatches;
        const includeByTransport = allTransportFiltersUnchecked || transportMatches;

        // Return true if both the product and transport filters match (if selected)
        return includeByProduct && includeByTransport && capacityMatches;
    })
  }
}

const filterPipelines = (pipelines, selectedNames = [], propKey = 'pipename', isVisible = true) => {
  if (!pipelines || !pipelines.features || !isVisible)
    return { type: 'FeatureCollection', features: [] };

  const normalizedSelected = (selectedNames || [])
    .map(name => name.trim().toLowerCase())
    .filter(Boolean);

  // If no filters selected, show all when overlay is visible
  if (normalizedSelected.length === 0) {
    return pipelines;
  }

  const filtered = pipelines.features.filter(feature => {
    const pipelineName = feature.properties?.[propKey];
    if (!pipelineName) return false;
    return normalizedSelected.includes(pipelineName.trim().toLowerCase());
  });

  return { type: 'FeatureCollection', features: filtered };
};



const csvLookup = useMemo(() => {
  const map = {};
  if (csvData && csvData.length > 0) {
    csvData.forEach(row => {
      const id = Number(row.OBJECTID);
      if (!isNaN(id)) {
        map[id] = row;
      }
    });
  }
  return map;
}, [csvData]);

const bufferedViewportBounds = useMemo(() => (
  getBufferedBounds(viewportBounds)
), [viewportBounds]);

const visibleListings = useMemo(() => (
  filterRowsByLatLng(listings, bufferedViewportBounds, (listing) => ({
    lat: listing.newLatitude,
    lng: listing.newLongitude,
  }))
), [listings, bufferedViewportBounds]);

const visibleInquirys = useMemo(() => (
  filterRowsByLatLng(inquirys, bufferedViewportBounds, (inquiry) => ({
    lat: inquiry.newLatitude,
    lng: inquiry.newLongitude,
  }))
), [inquirys, bufferedViewportBounds]);

const visibleProductTerminals = useMemo(() => (
  filterRowsByLatLng(productTerminals, bufferedViewportBounds, (feature) => ({
    lat: feature.Latitude,
    lng: feature.Longitude,
  }))
), [productTerminals, bufferedViewportBounds]);

const visibleTerminalDetail = useMemo(() => (
  filterRowsByLatLng(terminalDetail, bufferedViewportBounds, (feature) => ({
    lat: feature.Latitude,
    lng: feature.Longitude,
  }))
), [terminalDetail, bufferedViewportBounds]);

const visibleAdditionalTerminals = useMemo(() => (
  filterRowsByLatLng(additionalTerminals, bufferedViewportBounds, (feature) => ({
    lat: feature.Latitude,
    lng: feature.Longitude,
  }))
), [additionalTerminals, bufferedViewportBounds]);

const visibleIntermodal = useMemo(() => (
  filterRowsByLatLng(intermodal, bufferedViewportBounds, (feature) => ({
    lat: feature.geometry?.coordinates?.[1],
    lng: feature.geometry?.coordinates?.[0],
  }))
), [intermodal, bufferedViewportBounds]);



const getAllCrudePipelineNames = (geojson) => {
  const nameSet = new Set();

  geojson.features.forEach(feature => {
    const name = feature.properties?.pipename;
    if (name) nameSet.add(name.trim());
  });

  return Array.from(nameSet).sort();
};

const getAllCsvPipelineNames = (csvData) => {
  const pipelineSet = new Set();

  csvData.forEach(terminal => {
    const inPipes = terminal['Pipeline In'];
    const outPipes = terminal['Pipeline Out'];

    if (inPipes && inPipes !== 'No') {
      inPipes.split(/[,;/]/).forEach(name => {
        const trimmed = name.trim();
        if (trimmed) pipelineSet.add(trimmed);
      });
    }

    if (outPipes && outPipes !== 'No') {
      outPipes.split(/[,;/]/).forEach(name => {
        const trimmed = name.trim();
        if (trimmed) pipelineSet.add(trimmed);
      });
    }
  });

  return Array.from(pipelineSet).sort();
};

const getAllSources = (terminals) => {
  if (!terminals || terminals.length === 0) return [];
  const sources = terminals
    .map(t => t.source || t.Source || t.Company || '')
    .filter(Boolean)
    .map(s => String(s).trim())
    .filter(s => s.length > 0);
  return [...new Set(sources)].sort();
};

const normalizeOverlayRows = (records) => {
  if (!records) return [];

  return records.map((record, index) => {
    const properties = record?.properties ?? record ?? {};
    const row = { ...properties, _table_id: index };
    const coordinates = record?.geometry?.coordinates;

    if (Array.isArray(coordinates) && !Array.isArray(coordinates[0])) {
      row.Longitude = row.Longitude || coordinates[0];
      row.Latitude = row.Latitude || coordinates[1];
    }

    return row;
  });
};

const rackPricingTableRows = useMemo(() => (
  rackPriceCities.flatMap((city) => city.prices.map((price, index) => ({
    _table_id: `${price.symbol}-${price.assessDate || index}`,
    City: city.city,
    State: city.state,
    Product: price.productName || price.specification,
    'Product Category': price.productCategory,
    'Product Code': price.specCode,
    Symbol: price.symbol,
    Publication: price.publication,
    'Price (USC/gal)': price.value,
    Change: price.change,
    'Change Percent': price.changePercent,
    'Assessment Date': price.assessDate,
  })))
), [rackPriceCities]);

const overlayTableData = useMemo(() => ({
  product_detail: normalizeOverlayRows(terminalDetail),
  product: normalizeOverlayRows(productTerminals),
  additional_terminals: normalizeOverlayRows(additionalTerminals),
  intermodal: normalizeOverlayRows(intermodal),
  crude: normalizeOverlayRows(filteredCrudePipelines?.features),
  refined: normalizeOverlayRows(filteredRefinedPipelines?.features),
  padd: normalizeOverlayRows(padd?.features),
  bnsf: normalizeOverlayRows(railBNSF?.features),
  csx: normalizeOverlayRows(railCSX?.features),
  up: normalizeOverlayRows(railUP?.features),
  ns: normalizeOverlayRows(railNS?.features),
  marine: normalizeOverlayRows(marine?.features),
  rack_pricing: rackPricingTableRows,
}), [
  terminalDetail,
  productTerminals,
  additionalTerminals,
  intermodal,
  filteredCrudePipelines,
  filteredRefinedPipelines,
  padd,
  railBNSF,
  railCSX,
  railUP,
  railNS,
  marine,
  rackPricingTableRows,
]);

useEffect(() => {
  if (setOverlayTableData) {
    setOverlayTableData(overlayTableData);
  }
}, [overlayTableData, setOverlayTableData]);


useEffect(() => {
  if(Number(overlayAccess) === 1){
      fetchData();
  }
}, [overlayAccess,]);

useEffect(() => {
  if (!overlay.rack_pricing && !showRackPricingTable) return;

  const publication = rackPricingFilters.publication || 'LATEST';
  const morningRequest = loadRackPricing('TR').catch(() => null);
  const eveningRequest = publication === 'TR'
    ? Promise.resolve(null)
    : loadRackPricing('TE').catch(() => null);

  Promise.all([morningRequest, eveningRequest])
    .then(([morningData, eveningData]) => {
      setRackPriceCities(mergeRackPriceCities(
        morningData?.cities || [],
        eveningData?.cities || [],
        publication
      ));
    })
    .catch(() => setRackPriceCities([]));
}, [
  overlay.rack_pricing,
  showRackPricingTable,
  loadRackPricing,
  rackPricingFilters.publication,
]);

useEffect(() => {
  if (!overlay.rack_pricing || !maps || rackPriceCities.length === 0) return;
  if (rackPriceCities.every((city) => city.latitude && city.longitude)) return;

  const geocoder = new maps.Geocoder();
  let cancelled = false;
  let timer;
  const cacheKey = 'millsRackCityCoordinates';
  let coordinateCache = {};
  try {
    coordinateCache = JSON.parse(localStorage.getItem(cacheKey) || '{}');
  } catch (error) {
    coordinateCache = {};
  }

  const citiesToResolve = rackPriceCities.filter((city) => !city.latitude || !city.longitude);
  const resolveNext = (index) => {
    if (cancelled || index >= citiesToResolve.length) return;
    const city = citiesToResolve[index];
    const cached = coordinateCache[city.code];

    if (cached) {
      setRackPriceCities((previous) => previous.map((item) => (
        item.code === city.code ? { ...item, ...cached } : item
      )));
      resolveNext(index + 1);
      return;
    }

    geocoder.geocode({ address: `${city.city}, USA` }, (results, status) => {
      if (!cancelled && status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const coordinates = { latitude: location.lat(), longitude: location.lng() };
        coordinateCache[city.code] = coordinates;
        localStorage.setItem(cacheKey, JSON.stringify(coordinateCache));
        setRackPriceCities((previous) => previous.map((item) => (
          item.code === city.code ? { ...item, ...coordinates } : item
        )));
      }
      timer = window.setTimeout(() => resolveNext(index + 1), 150);
    });
  };

  resolveNext(0);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}, [overlay.rack_pricing, maps, rackPriceCities]);

useEffect(() => {
  if (!map || !maps || !citySearch || !citySearch.place_id) {
    return;
  }

  if (!maps.places) {
    return;
  }

  const placesService = new maps.places.PlacesService(map);
  placesService.getDetails({ placeId: citySearch.place_id, fields: ['geometry'] }, (place, status) => {
    if (status !== maps.places.PlacesServiceStatus.OK || !place || !place.geometry) {
      return;
    }

    if (place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport);
    } else if (place.geometry.location) {
      map.setCenter(place.geometry.location);
      map.setZoom(9);
    }
  });
}, [citySearch, map, maps]);

  const handleApiLoaded = (mapInstance, mapsInstance) => {
    setMap(mapInstance);
    setMaps(mapsInstance);
  };

  const handleMapChange = ({ bounds }) => {
    if (!bounds || !bounds.nw || !bounds.se) return;

    const nextKey = [
      bounds.nw.lat,
      bounds.nw.lng,
      bounds.se.lat,
      bounds.se.lng,
    ].map((value) => Number(value).toFixed(3)).join('|');

    setViewportBounds((previousBounds) => (
      previousBounds && previousBounds.key === nextKey
        ? previousBounds
        : { ...bounds, key: nextKey }
    ));
  };

  return(
    <div style={{ height, minHeight: 260, width: '100%', position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          right: 72,
          zIndex: 1,
          backgroundColor: 'rgba(36, 47, 62, 0.92)',
          padding: 6,
          borderRadius: 4
        }}
      >
        <MapCitySearch value={citySearch} onChange={setCitySearch} />
      </div>
      <GoogleMapReact
        bootstrapURLKeys={{
          key:'AIzaSyBTp8nnXh_LBIFetGatTUUEzjsT58DCg9A',
          libraries: ['places']
        }}
        defaultCenter={{lat:37,lng:-95}}
        defaultZoom={4}
        hoverDistance={0.1}
        options={defaultMapOptions}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        onChange={handleMapChange}
      >
        {showListings && visibleListings.map((listing) => (
          <MapComponent
            key = {listing['listing_id']}
            lat = {parseFloat(listing['newLatitude'])}
            lng = {parseFloat(listing['newLongitude'])}
            id = {listing['listing_id']}
            value = {listing['listing_id']}
            volume = {listing['working_capacity']}
            price = {listing['ask_price']}
            product = {listing['product_category']}
          />
        ))
        }
        {showInquirys && visibleInquirys.map((inquiry) => (inquiry['latitude'] &&
          <InquiryMapComponent
            key = {inquiry['inquiry_id']}
            lat = {parseFloat(inquiry['newLatitude'])}
            lng = {parseFloat(inquiry['newLongitude'])}
            id = {inquiry['inquiry_id']}
            value = {inquiry['inquiry_id']}
            min_volume = {inquiry['min_volume']}
            max_volume = {inquiry['max_volume']}
            price = {inquiry['bid_price']}
            product = {inquiry['product']}
          />
        ))
        }
        {overlay.product && visibleProductTerminals && visibleProductTerminals.map((feature, index)=> (
            <TerminalComponent
              key={index}
              lat={feature.Latitude}
              lng={feature.Longitude}
              feature={feature}
              type='product'
              csvLookup={csvLookup}
            />
        ))
        }
        {overlay.product_detail && visibleTerminalDetail && visibleTerminalDetail.map((feature, index) => (
          <TerminalCsvComponent
            key={index}
            lat={parseFloat(feature.Latitude)}
            lng={parseFloat(feature.Longitude)}
            feature={feature}
            type = {'detail'}
          />
        ))}
        {overlay.additional_terminals && visibleAdditionalTerminals && visibleAdditionalTerminals.map((feature, index) => (
          <TerminalCsvComponent
            key = {index}
            lat={parseFloat(feature.Latitude)}
            lng={parseFloat(feature.Longitude)}
            feature={feature}
            type = {'additional'}
          />
        ))}
        {overlay.rack_pricing && filteredRackPriceCities.filter((city) => city.latitude && city.longitude).map((city) => (
          <RackPriceMarker
            key={city.code}
            lat={Number(city.latitude)}
            lng={Number(city.longitude)}
            city={city}
            publication={rackPricingFilters.publication || 'LATEST'}
            onPublicationChange={(publication) => setRackPricingFilters((previous) => ({
              ...previous,
              publication,
            }))}
          />
        ))}
        {overlay.intermodal && visibleIntermodal && visibleIntermodal.map((feature, index) => (
          <TerminalComponent
            key={index}
            lat = {feature.geometry.coordinates[1]}
            lng = {feature.geometry.coordinates[0]}
            feature={feature}
            type = 'intermodal'
          />
        ))

        }
        <PipelineComponent map={map}
          maps={maps}
          pipelineData={filteredCrudePipelines}
          strokeColor = {'#8B0000'}
          visible = {overlay.crude}
        />
        <PipelineComponent
          map={map}
          maps={maps}
          pipelineData={filteredRefinedPipelines}
          strokeColor = {'#00008B'}
          visible = {overlay.refined}
        />
        <RailComponent
          map={map}
          maps={maps}
          railData={railBNSF}
          strokeColor={'#2F4F4F'}
          visible = {overlay.bnsf}
        />
        <RailComponent
          map={map}
          maps={maps}
          railData={railUP}
          strokeColor={'#000000'}
          visible = {overlay.up}
        />
        <RailComponent
          map={map}
          maps={maps}
          railData={railCSX}
          strokeColor={'#006400'}
          visible = {overlay.csx}
        />
        <RailComponent
          map={map}
          maps={maps}
          railData={railNS}
          strokeColor={'#8B4513'}
          visible = {overlay.ns}
        />
        <MarineComponent
          map={map}
          maps={maps}
          marineData={marine}
          strokeColor={'#456898'}
          visible = {overlay.marine}
        />
        <PADDComponent
            map={map}
            maps={maps}
            paddData={padd}
            strokeColor="#FF0000"
            fillColor="#FF0000"
            visible={overlay.padd}
          />
      </GoogleMapReact>
    </div>
  )
}
